import React,{useEffect,useRef,useState} from 'react';
import * as Fi from 'react-icons/fi';
import {Link} from 'react-router-dom';

import {dbService} from '../services/dbService';

import {APP_ID,authenticate,restoreSession,getStoredSession,clearSession,BPA_SESSION_STORAGE_KEY,requestPasswordReset,
 getSheetConnection,saveSheetConnection} from '../lib/bpaAuth.js';
import {setGoogleScope,getSheetId,rememberSheetId,authorizeGoogle,isGoogleAuthorized,isGoogleConfigured,
 createBrandedWorkbook,verifyWorkbook,latestRevision,uploadSnapshot,downloadSnapshot,markRestored,clearGoogleAuth,getBaseline,initialiseSheetFromLocal,formatWorkbookRows,reconcileIdenticalLocalBackup,reconcileSafeLocalChanges} from '../lib/expenseGoogleSheets.js';
import {localDirtyMarker,acknowledgeLocalDirty,signalGoogleReady,markLocalDirty} from '../lib/expenseAutoSyncState.js';

// Consolidated from src/components/GoogleSetupProgress.jsx; keep its behavior intact.
const GoogleSetupProgress = (()=>{


// These are completed-operation milestones, NOT a time estimate.
// The UI moves only when an actual Google API or BPA registration step begins.
const FLOWS = {
 new: [
  ['preparing','Preparing your local financial records'],
  ['creating','Creating your Google spreadsheet'],
  ['formatting','Adding and styling the BPA worksheets'],
  ['verifying','Checking spreadsheet structure'],
  ['checking-history','Checking that the new workbook is safe to use'],
  ['uploading','Saving your first financial backup'],
  ['committing','Recording the completed backup revision'],
  ['readback','Reading back and verifying your saved records'],
  ['registering','Linking the workbook to your BPA account'],
  ['ready','Your workspace is ready'],
 ],
 'existing-empty': [
  ['finding','Finding your existing BPA workbook'],
  ['verifying','Checking spreadsheet structure'],
  ['checking-history','Checking previous backup history'],
  ['uploading','Saving the first financial backup'],
  ['committing','Recording the backup revision'],
  ['readback','Checking saved financial records'],
  ['registering','Linking the workbook to your BPA account'],
  ['ready','Your workbook is connected'],
 ],
 reconnect: [
  ['finding','Finding your existing BPA workbook'],
  ['verifying','Checking spreadsheet structure'],
  ['checking-history','Confirming the existing backup history'],
  ['registering','Linking the workbook to your BPA account'],
  ['ready','Your existing workbook is connected'],
 ],
};
function setupMilestones(mode){return FLOWS[mode]||FLOWS.new;}
function setupPercent(mode,stage){
 const steps=setupMilestones(mode),index=steps.findIndex(([key])=>key===stage);
 return index<0?0:Math.round(index/(steps.length-1)*100);
}
function GoogleSetupProgress({progress}){
 if(!progress)return null;
 const {mode,stage,status,note}=progress;
 const steps=setupMilestones(mode);
 const index=Math.max(0,steps.findIndex(([key])=>key===stage));
 const percentage=setupPercent(mode,stage);
 const working=status==='working',success=status==='complete',warning=status==='warning';
 const title=success?'Setup complete':warning?'Workbook saved; one step needs attention':status==='error'?'Setup paused safely':'Setting up your Google Sheets workspace';
 return <section aria-label="Google Sheets setup progress" className={'rounded-2xl border p-4 sm:p-5 space-y-4 '+(success?'border-emerald-200 bg-emerald-50':warning?'border-amber-200 bg-amber-50':status==='error'?'border-rose-200 bg-rose-50':'border-blue-200 bg-blue-50')}>
   <div className="flex items-start gap-3">
    <span aria-hidden="true" className={'rounded-xl p-2 '+(success?'bg-emerald-100 text-emerald-700':warning?'bg-amber-100 text-amber-800':status==='error'?'bg-rose-100 text-rose-700':'bg-white text-blue-700')}>
     {success?<Fi.FiCheckCircle size={23}/>:warning?<Fi.FiAlertTriangle size={23}/>:status==='error'?<Fi.FiAlertCircle size={23}/>:<Fi.FiLoader size={23} className="animate-spin"/>}
    </span>
    <div className="min-w-0 flex-1">
      <h3 className="font-bold text-slate-900 text-base">{title}</h3>
      <p className="text-sm text-slate-700 mt-1" aria-live="polite" aria-atomic="true">{note||steps[index][1]}</p>
    </div>
   </div>
   <div>
    <div className="flex justify-between gap-2 mb-1.5 text-xs font-semibold text-slate-600">
      <span>{working?'Current milestone':success?'All milestones verified':warning?'Waiting for BPA registration':'Last reached milestone'}</span>
      <span className="tabular-nums">{percentage}%</span>
    </div>
    <div className="h-2 w-full rounded-full bg-slate-200 overflow-hidden" role="progressbar" aria-label="Verified Google setup milestones" aria-valuenow={percentage} aria-valuemin={0} aria-valuemax={100} aria-valuetext={steps[index][1]}>
      <div className={'h-full rounded-full transition-all duration-300 '+(success?'bg-emerald-600':warning?'bg-amber-500':status==='error'?'bg-rose-500':'bg-[#0176D3]')} style={{width:percentage+'%'}}/>
    </div>
   </div>
   <ol className="grid sm:grid-cols-2 gap-2" aria-label="Setup stages">
    {steps.map(([key,label],position)=>{
      const done=success?position<=index:position<index;
      const active=position===index&&!success;
      return <li key={key} className="flex items-start gap-2 text-sm">
       <span aria-hidden="true" className={'mt-0.5 shrink-0 '+(done?'text-emerald-700':active?'text-blue-700':'text-slate-400')}>
        {done?<Fi.FiCheckCircle/>:active&&working?<Fi.FiLoader className="animate-spin"/>:active&&status==='error'?<Fi.FiAlertCircle/>:active&&warning?<Fi.FiAlertTriangle/>:<Fi.FiCircle/>}
       </span>
       <span className={done?'text-slate-600':active?'text-slate-900 font-semibold':'text-slate-500'}>{label}</span>
      </li>;
    })}
   </ol>
   {working&&<p className="text-xs text-slate-600">Steps update as Google confirms them. Large backups can take longer. Keep this tab open and do not click Create again.</p>}
  </section>;
}

return GoogleSetupProgress;
})();

const btn='rounded-xl px-4 py-3 text-sm font-semibold min-h-11 cursor-pointer transition-all duration-150 hover:shadow-sm active:translate-y-px focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none disabled:transform-none';
function downloadJSON(data,name){const url=URL.createObjectURL(new Blob([JSON.stringify(data,null,2)],{type:'application/json'}));
 const a=document.createElement('a');a.href=url;a.download=name;document.body.append(a);a.click();a.remove();setTimeout(()=>URL.revokeObjectURL(url),3000);}
export default function GoogleSyncPanel(){
 const [account,setAccount]=useState(null),[email,setEmail]=useState(''),[password,setPassword]=useState('');
 const [sheetInput,setSheetInput]=useState(''),[sheet,setSheet]=useState(''),[remote,setRemote]=useState(null);
 const [busy,setBusy]=useState(''),[message,setMessage]=useState(''),[error,setError]=useState(''),[registerPending,setRegisterPending]=useState(false);
 const [recoverMode,setRecoverMode]=useState(false),[confirmation,setConfirmation]=useState('');
 const [googleConnected,setGoogleConnected]=useState(false);
 const currentUserRef=useRef(null),progressRef=useRef(null);
 const [confirmNew,setConfirmNew]=useState(false),[legacyConfirm,setLegacyConfirm]=useState(false);
 const [workbookChoice,setWorkbookChoice]=useState(''),[googleStatus,setGoogleStatus]=useState('');
 const [setupProgress,setSetupProgress]=useState(null),[setupComplete,setSetupComplete]=useState(null);
 const [syncActivity,setSyncActivity]=useState(null);
 const [restoreFinished,setRestoreFinished]=useState(null);
 const restoreStages=[['starting','Preparing recovery'],['verify','Verifying Google backup'],['safety','Saving a local safety copy'],['settings','Preserving device preferences'],['import','Restoring records locally'],['finalise','Confirming restored revision'],['complete','Restore completed']];
 const syncAction=(key,stage,message,status='running')=>setSyncActivity({key,stage,message,status});
 const reconcileWorkbook=async(id,session,showProgress=true)=>{
   const central=await getSheetConnection(session.token);
   if(central?.spreadsheetId!==id)
     throw new Error('Applications Hub does not confirm this Google Sheet for this customer. Select Retry registration before syncing.');
   const localBackup=await dbService.exportData();
   const result=await reconcileSafeLocalChanges(id,localBackup,(stage,message)=>{
     if(showProgress)syncAction('check',stage,message);
   });
   if(result.kind==='additions'){
     markLocalDirty(session.record.id); // Pending edit survives reloads until Google confirms it.
     signalGoogleReady();
   }else if(result.kind==='identical'){
     const marker=localDirtyMarker(session.record.id);
     if(marker)acknowledgeLocalDirty(session.record.id,marker);
     signalGoogleReady();
   }
   return result;
 };
 const connected=Boolean(account && sheet),configured=Boolean(APP_ID&&isGoogleConfigured());
 const authorised=isGoogleAuthorized();
 useEffect(()=>{
   // Keep the setup indicator in view when the customer starts a long
   // operation, especially on phones. Do not jump for every API milestone.
   if(setupProgress?.status==='working'&&['preparing','finding'].includes(setupProgress.stage))
     progressRef.current?.scrollIntoView({behavior:'smooth',block:'nearest'});
 },[setupProgress?.mode,setupProgress?.stage,setupProgress?.status]);
 const advance=(stage,note)=>setSetupProgress(previous=>{
   if(!previous)return previous;
   // Keep the visual stage in sync with real backend checkpoints, without
   // briefly resetting progress when an internal milestone has no own row.
   const displayStage=({structured:'checking-history','backup-verified':'readback'})[stage]||stage;
   return {...previous,stage:displayStage,note:note||'',status:'working'};
 });
 const startSetup=(mode,stage)=>{setSetupComplete(null);setSetupProgress({mode,stage,status:'working',note:''})};
 const finishSetup=(registered,kind,initialBackup,verifiedBackup=true)=>{
   const ready=registered&&verifiedBackup;
   const note=!registered
    ? 'Your workbook is preserved, but BPA registration has not succeeded. Retry registration.'
    :!verifiedBackup
      ? 'Workbook linked, but this browser still needs to reconcile its records with Google. Use Check Sheet below.'
      :initialBackup
        ? 'Your Google Sheet and initial financial backup are verified. You can add transactions.'
        : 'Your existing workbook is verified. Its saved financial records were not replaced.';
   setSetupProgress(p=>p?{...p,stage:ready?'ready':'registering',status:ready?'complete':'warning',note}:p);
   setSetupComplete({kind,registered,initialBackup,verifiedBackup});
 };
 async function refreshRemote(id){const info=await latestRevision(id);setRemote(info);return info;}
 useEffect(()=>{
  if(!APP_ID)return;
  let active=true;
  const useSession=async session=>{
   if(!active)return;
   if(!session?.token||!session.record?.id){
    currentUserRef.current=null;
    setAccount(null);setSheet('');setSheetInput('');setRemote(null);
    setGoogleConnected(false);setGoogleStatus('');setSetupProgress(null);setSetupComplete(null);
    setWorkbookChoice('');clearGoogleAuth();return;
   }
   const switched=currentUserRef.current!==session.record.id;
   currentUserRef.current=session.record.id;
   if(switched){
    setGoogleScope(session.record.id);
    setGoogleConnected(false);setGoogleStatus('');setSheet('');setSheetInput('');setRemote(null);
    setSetupProgress(null);setSetupComplete(null);setWorkbookChoice('');
   }
   setAccount(session);
   if(switched){
    const central=await getSheetConnection(session.token).catch(error=>{
     if(error.status===401||error.status===403)throw error;
     return null;
    });
    if(!active||currentUserRef.current!==session.record.id)return;
    const id=central?.spreadsheetId||getSheetId();
    if(central?.spreadsheetId)rememberSheetId(central.spreadsheetId);
    if(id){setSheet(id);setSheetInput(id);
      // Check Sheet is explicit; opening Settings must not consume Google read quota.
    }
   }
  };
  // BPA sessions are already stored in same-origin localStorage. A storage
  // event updates other open tabs instantly. It does not share Google tokens
  // or attempt a second BPA login, and avoids token-refresh ping-pong.
  const onStorage=event=>{
   if(event.storageArea!==localStorage||event.key!==BPA_SESSION_STORAGE_KEY)return;
   useSession(getStoredSession()).catch(error=>{if(active)setError(error.message)});
  };
  window.addEventListener('storage',onStorage);
  (async()=>{
   try{await useSession(await restoreSession())}
   catch(error){if(active)setError(error.message)}
  })();
  return()=>{active=false;window.removeEventListener('storage',onStorage)};
 },[]);
 useEffect(()=>{
   // Header auto-sync works while the customer uses any finance screen.
   // Keep this panel's visible revision/counts current when it is open.
   const update=event=>{
     if(event.detail?.userId!==account?.record?.id || event.detail?.sheetId!==sheet)return;
     setRemote({revision:event.detail.revision,counts:event.detail.counts});
   };
   window.addEventListener('bpa:google-autosynced',update);
   return ()=>window.removeEventListener('bpa:google-autosynced',update);
 },[account?.record?.id,sheet]);
 const run=async(key,work)=>{
   if(busy)return;
   setBusy(key);setError('');setMessage('');
   if(['check','sync','format','download','restore'].includes(key)){
     const intro={check:'Contacting Google to check your workbook…',sync:'Preparing your saved financial records…',
       format:'Checking Google worksheet formatting…',download:'Reading a verified Google backup…',
       restore:'Preparing your financial recovery…'};
     syncAction(key,'starting',intro[key]);
   }
   try{await work()}
   catch(e){
     setError(e.message);
     if(['check','sync','format','download','restore'].includes(key))
       syncAction(key,'error',e.message,'error');
     if(key==='create'||key==='connect')setSetupProgress(p=>p?{...p,status:'error',note:e.message}:p);
     if(key==='google')setGoogleStatus('error');
   }finally{setBusy('')}
 };
 const signIn=e=>{e.preventDefault();run('login',async()=>{const session=await authenticate(email,password);
   currentUserRef.current=session.record.id;
   setGoogleScope(session.record.id);setAccount(session);setPassword('');
   const central=await getSheetConnection(session.token).catch(e=>{if(e.status===401||e.status===403)throw e;return null});
   const id=central?.spreadsheetId||getSheetId();if(central?.spreadsheetId)rememberSheetId(central.spreadsheetId);if(id){setSheet(id);setSheetInput(id)}
   setMessage('BPA account verified. Next authorise Google separately.');})};
 const connectGoogle=()=>run('google',async()=>{
   setGoogleStatus('authorising');
   await authorizeGoogle();
   setGoogleConnected(true);setGoogleStatus('authorised');
   // Authorisation alone does not need another Google revision read.
   signalGoogleReady(); // Reconcile edits made before this browser session was authorised.
   setMessage(sheet?'Google is authorised. Continue with your linked workbook below.':'Google is authorised. Next, create a new BPA-branded Sheet or connect an existing BPA workbook.');
 });
 async function ensureCentralRegistration(id,label){
   // The new CNAME may already have a registered Sheet in Applications Hub.
   // Reuse that exact entitlement reference instead of writing it again.
   const existing=await getSheetConnection(account.token);
   if(existing?.spreadsheetId===id)return existing;
   if(existing?.spreadsheetId&&existing.spreadsheetId!==id)
     throw new Error('Another Google workbook is already registered for this BPA customer. Nothing was replaced. Review the App Hub connection before changing workbooks.');
   return saveSheetConnection(account.token,id,label);
 }
 async function register(id,label){
   rememberSheetId(id);setSheet(id);setSheetInput(id);
   try{const saved=await ensureCentralRegistration(id,label);
    if(saved.spreadsheetId!==id)throw new Error('Applications Hub returned the wrong Sheet ID.');
    setRegisterPending(false);
    return true;
   }catch(e){
    if(e.status===401||e.status===403)throw e;
    setRegisterPending(true);setMessage('Google Sheet is verified and usable, but BPA registration is pending. Use Retry registration. '+e.message);
    return false;
   }
 }
 // Every first connection to an EMPTY workbook commits a complete initial revision.
 // Existing committed workbooks are never overwritten or restored automatically.
 const connectExisting=()=>run('connect',async()=>{
   startSetup('reconnect','finding');
   if(!isGoogleAuthorized())throw new Error('Authorise Google before connecting a Sheet.');
   advance('verifying');
   const workbook=await verifyWorkbook(sheetInput);
   advance('checking-history');
   const previous=await latestRevision(workbook.id);
   let initial,dirtyBeforeInitial='';
   if(!previous){
     setSetupProgress(p=>p?{...p,mode:'existing-empty'}:p);
     approveLocalOwnership();
     dirtyBeforeInitial=localDirtyMarker(account.record.id);
     const localBackup=await dbService.exportData();
     initial=await initialiseSheetFromLocal(workbook.id,localBackup,advance);
   }
   advance('registering');
   const registered=await register(workbook.id,workbook.title);
   setRemote(previous||initial||null);
   let comparison=null;
   if(registered&&previous&&!getBaseline(workbook.id)){
     syncAction('check','comparison','Comparing your local data with the verified Google backup…');
     comparison=await reconcileWorkbook(workbook.id,account);
     const note=comparison.kind==='additions'
       ?comparison.totalAdditions+' local record(s) were not in Google and are now queued for automatic backup.'
       :comparison.kind==='identical'?'Existing local records match Google. Automatic backup is ready.'
       :comparison.reason;
     syncAction('check','compared',note,
       comparison.kind==='conflict'?'warning':'success');
   }
   const verifiedBackup=Boolean(getBaseline(workbook.id));
   finishSetup(registered,previous?'existing':'existing-empty',Boolean(initial?.created),verifiedBackup);
   if(registered){
     if(initial?.created)acknowledgeLocalDirty(account.record.id,dirtyBeforeInitial);
     signalGoogleReady();
   }
   setMessage(!registered
     ? 'Existing workbook linked, but BPA registration is pending. Retry registration.'
     :verifiedBackup
       ?comparison?.kind==='additions'
         ?comparison.totalAdditions+' saved local record(s) are queued for Google backup.'
         :'Existing workbook verified with your customer account. Continue working.'
       :'Workbook linked, but its data differs from this device. Review the Check Sheet result below before uploading.');
 });
 const createNew=()=>run('create',async()=>{
   startSetup('new','preparing');
   if(!isGoogleAuthorized())throw new Error('Authorise Google first.');
   if(sheet||getSheetId())throw new Error('A Sheet is already linked. Reconnect that workbook rather than create a duplicate.');
   if(!confirmNew)throw new Error('Confirm creation in the app before continuing.');
   approveLocalOwnership();
   // Browser storage belongs to the current origin. On a new custom domain,
   // verify the central App Hub connection at creation time (fail closed).
   // Never create another workbook if the customer already has a registered one.
   const existing=await getSheetConnection(account.token);
   if(existing?.spreadsheetId){
     rememberSheetId(existing.spreadsheetId);
     setSheet(existing.spreadsheetId);
     setSheetInput(existing.spreadsheetId);
     setWorkbookChoice('existing');
     setConfirmNew(false);
     throw new Error('This BPA customer already has a registered Google Sheet. Connect and verify that workbook instead of creating a duplicate.');
   }
   const dirtyBeforeInitial=localDirtyMarker(account.record.id);
   const localBackup=await dbService.exportData();
   let workbook;
   try{
     workbook=await createBrandedWorkbook(advance);
     setConfirmNew(false);
     // Retain the reference even when first sync or central registration fails.
     setSheet(workbook.id);setSheetInput(workbook.id);
     const initial=await initialiseSheetFromLocal(workbook.id,localBackup,advance);
     advance('registering');
     const registered=await register(workbook.id,workbook.title);
     setRemote(initial);
     finishSetup(registered,'new',true);
     if(registered){acknowledgeLocalDirty(account.record.id,dirtyBeforeInitial);signalGoogleReady();}
     setMessage('Your BPA workbook is created and the initial financial backup was verified.'+
       (registered?' BPA Applications Hub registration confirmed.':' BPA registration is pending. Use Retry registration.'));
   }catch(error){
     const createdId=workbook?.id||getSheetId();
     if(createdId){setSheet(createdId);setSheetInput(createdId);setConfirmNew(false);}
     throw error;
   }
 });
 const approveLocalOwnership=()=>{
   const id=account?.record?.id;
   if(!id || dbService.getWorkspaceUser()!==id)
     throw new Error('The active financial workspace does not match your verified BPA customer account.');
 };
 const importLegacy=()=>run('legacy',async()=>{
   approveLocalOwnership();
   if(!legacyConfirm)throw new Error('Confirm ownership before importing original browser records.');
   await dbService.importLegacyIntoWorkspace({confirmed:true});
   setLegacyConfirm(false);
   setMessage('Original browser data imported without deleting its backup. Reloading your customer workspace.');
   window.location.reload();
 });
 const syncNow=()=>run('sync',async()=>{
   approveLocalOwnership();
   if(!connected||!isGoogleAuthorized())throw new Error('Connect and authorise your Google Sheet first.');
   if(!getBaseline(sheet))throw new Error('Your device has no verified Google revision. Use Check Sheet to compare records safely before uploading.');
   const marker=localDirtyMarker(account.record.id);
   syncAction('sync','snapshot','Preparing your saved financial transactions and budgets…');
   const before=await dbService.exportData();
   const result=await uploadSnapshot(sheet,before,stage=>{
     const labels={uploading:'Uploading changed records to Google Sheets…',
       committing:'Recording and confirming your new SyncHistory revision…',
       readback:'Reading the backup back from Google for verification…',
       'backup-verified':'Google confirmed the completed backup.'};
     syncAction('sync',stage,labels[stage]||'Checking financial records…');
   });setRemote(result);
   acknowledgeLocalDirty(account.record.id,marker);
   signalGoogleReady();
   const msg='Backup verified: revision '+result.revision+'. Local data was preserved.';
   syncAction('sync','complete',msg,'success');
   setMessage(msg+(result.formattingWarning?' '+result.formattingWarning:''));});
 const checkRemote=()=>run('check',async()=>{
   approveLocalOwnership();
   if(!isGoogleAuthorized())throw new Error('Reconnect Google first.');
   syncAction('check','structure','Checking workbook structure and permissions…');
   await verifyWorkbook(sheet);
   syncAction('check','history','Reading the latest completed SyncHistory revision…');
   const latest=await refreshRemote(sheet);
   if(!latest?.revision){
     syncAction('check','empty','There is no completed Google revision yet. No data was changed. Review the initial backup setup.','warning');
     return;
   }
   const base=getBaseline(sheet);
   if(base&&base!==latest.revision){
     const msg='This device has revision '+base+' but Google has '+latest.revision+
       '. Automatic backup is paused until the two revisions are reconciled. Neither copy was overwritten.';
     syncAction('check','conflict',msg,'warning');
     setMessage(msg);
     return;
   }
   if(!base){
     syncAction('check','ownership','Confirming the workbook belongs to your BPA customer account…');
     const result=await reconcileWorkbook(sheet,account);
     if(result.kind==='additions'){
       const msg=result.totalAdditions+' locally added record(s) were not in Google. Existing Google records matched. These changes are now queued for automatic backup.';
       syncAction('check','queued',msg,'success');
       setMessage(msg);
     }else if(result.kind==='identical'){
       const msg='All local financial records match the verified Google backup. Automatic backup can resume.';
       syncAction('check','verified',msg,'success');
       setMessage(msg);
     }else{
       const msg='Comparison requires review: '+result.reason+' Automatic upload remains paused. No financial data was changed.';
       syncAction('check','conflict',msg,'warning');
       setMessage(msg);
     }
   }else{
     const msg='Google revision '+latest.revision+' matches this device. No records were changed.';
     syncAction('check','verified',msg,'success');
     setMessage(msg);
     signalGoogleReady();
   }
 });
 const retryRegistry=()=>run('registry',async()=>{const verified=await verifyWorkbook(sheet);
   const saved=await ensureCentralRegistration(verified.id,verified.title);
   if(saved.spreadsheetId!==verified.id)throw new Error('Central registration did not confirm the Sheet ID.');
   setRegisterPending(false);setMessage('BPA Applications Hub reference saved.');
   signalGoogleReady();
   setSetupProgress(p=>p&&p.status==='warning'?{...p,stage:'ready',status:'complete',note:'Your workbook and its BPA registration are now confirmed.'}:p);
   setSetupComplete(p=>p?{...p,registered:true}:p);
 });
 const repairRows=()=>run('format',async()=>{if(!connected||!isGoogleAuthorized())throw new Error('Reconnect Google first.');
   const repaired=await formatWorkbookRows(sheet);
   const msg='Formatting verified across '+repaired.formattedTabs+' tabs. No records or revisions were changed.';
   syncAction('format','complete',msg,'success');setMessage(msg);});
 const downloadRemote=()=>run('download',async()=>{const back=await downloadSnapshot(sheet);
   syncAction('download','writing','Preparing the verified backup for download…');
   downloadJSON(back,'expense-manager-google-backup-'+back.revision+'.json');
   syncAction('download','complete','Google backup downloaded without changing local records.','success');
   setMessage('Verified Google backup downloaded; no local data was changed.');});
 const restoreRemote=()=>run('restore',async()=>{
   approveLocalOwnership();
   if(confirmation!=='RESTORE')throw new Error('Type RESTORE to confirm.');
   // Every stage is tied to an actual operation, never a simulated percentage.
   syncAction('restore','verify','Reading and checking the Google workbook, revision history and financial checksums…');
   const back=await downloadSnapshot(sheet);
   // Request the local safety download before any browser records are replaced.
   syncAction('restore','safety','Preparing your current local records for safety download before replacement…');
   const current=await dbService.exportData();
   downloadJSON(current,'expense-manager-pre-google-restore-'+new Date().toISOString().slice(0,10)+'.json');
   syncAction('restore','settings','Preserving this device\'s backup preferences without changing Google data…');
   const excluded=['backupFolder','lastBackup','autoBackup','autoBackupInterval'];
   const deviceOnly=current.data.settings.filter(row=>excluded.includes(row.key));
   back.data.settings=[...back.data.settings.filter(row=>!excluded.includes(row.key)),...deviceOnly];
   syncAction('restore','import','Restoring verified financial records to this device in one atomic operation…');
   await dbService.importData(back);
   syncAction('restore','finalise','Recording the verified Google revision for this device…');
   await markRestored(sheet,back.revision,back);
   setRecoverMode(false);setConfirmation('');
   setRemote({revision:back.revision,counts:back.counts});
   const done='Verified Google revision '+back.revision+' has been restored. Open the workspace to load the restored records.';
   setMessage(done);
   syncAction('restore','complete',done,'success');
   // Do not reload until the customer has seen an explicit success message.
   setRestoreFinished({revision:back.revision});
 });
 const logOut=()=>{clearSession();window.dispatchEvent(new Event('bpa:auth-changed'));currentUserRef.current=null;clearGoogleAuth();setAccount(null);setSheet('');setGoogleConnected(false);setGoogleStatus('');setSetupProgress(null);setSetupComplete(null);setWorkbookChoice('');setRemote(null);setRegisterPending(false);setMessage('BPA session cleared. Local finance data was not deleted.')};
 const localBase=sheet?getBaseline(sheet):'';
 return <section aria-label="Google Sheets sync" className="min-w-0 space-y-5 p-3 sm:p-4 md:p-6">
   {(busy==='restore'||restoreFinished||(syncActivity?.key==='restore'&&syncActivity.status==='error'))&&<div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/65 p-4" role="dialog" aria-modal="true" aria-label="Google Sheet restore progress">
     <div className="w-full max-w-lg space-y-4 rounded-2xl bg-white p-5 shadow-2xl sm:p-6">
       <div className="flex items-center gap-3">
         <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-blue-50 text-[#0176D3]">{restoreFinished?<Fi.FiCheckCircle size={22}/>:syncActivity?.status==='error'?<Fi.FiAlertCircle size={22}/>:<Fi.FiRefreshCw className="animate-spin" size={22}/>}</span>
         <div><h3 className="font-bold text-[#032D60]">Restoring your financial workspace</h3><p className="text-sm text-slate-600">{restoreFinished?'Your restore has completed successfully.':syncActivity?.status==='error'?'Restore needs attention. Review the error before attempting recovery again.':'Google records are being verified and restored on this device.'}</p></div>
       </div>
       <ol className="space-y-2" aria-label="Verified restore stages">
         {restoreStages.map(([key,label],i)=>{
           const position=restoreStages.findIndex(([id])=>id===syncActivity?.stage);
           const done=Boolean(restoreFinished)||(!['error','complete'].includes(syncActivity?.stage)&&position>i),active=!restoreFinished&&syncActivity?.status!=='error'&&position===i;
           return <li key={key} className={'flex items-center gap-3 rounded-lg px-3 py-2 text-sm '+(done?'bg-emerald-50 text-emerald-800':active?'bg-blue-50 text-blue-900':'bg-slate-50 text-slate-500')}>
             {done?<Fi.FiCheckCircle className="shrink-0"/>:active?<Fi.FiLoader className="shrink-0 animate-spin"/>:<Fi.FiCircle className="shrink-0"/>}
             <span className={done||active?'font-semibold':''}>{label}</span>
           </li>;
         })}
       </ol>
       <p className={'text-sm leading-6 '+(syncActivity?.status==='error'?'font-semibold text-rose-800':'text-slate-700')} role={syncActivity?.status==='error'?'alert':'status'} aria-live="polite" aria-atomic="true">{syncActivity?.message||'Preparing your financial records…'}</p>
       {restoreFinished?<button type="button" onClick={()=>window.location.reload()} className={btn+' w-full bg-[#0176D3] text-white'}>Open restored workspace</button>:
        syncActivity?.status==='error'?<button type="button" onClick={()=>{setSyncActivity(null);setRecoverMode(true);setConfirmation('')}} className={btn+' w-full border border-rose-200 bg-white text-rose-900'}>Close and review recovery options</button>:
         <p className="text-xs text-slate-500">Keep this page open. Existing financial records will not be replaced unless the Google backup passes validation.</p>}
     </div>
   </div>}

  <div className="flex min-w-0 items-start gap-3 sm:items-center">
   <span className="shrink-0 p-3 rounded-xl bg-blue-50 text-blue-700"><Fi.FiCloud size={22}/></span>
   <div className="min-w-0"><h2 className="break-words text-lg font-bold text-slate-900 sm:text-xl">Google Sheets setup & recovery</h2>
    <p className="text-sm text-slate-600">Connect your own Google account, choose a workbook, and follow each verified setup step.</p>
   </div>
  </div>
  <div className="grid min-w-0 grid-cols-1 gap-2 min-[360px]:grid-cols-2 md:grid-cols-4" aria-label="Google setup guide">
    {[
      {label:'BPA account',done:Boolean(account),active:!account},
      {label:'Google permission',done:authorised,active:Boolean(account)&&!authorised},
      {label:'Choose workbook',done:Boolean(sheet),active:authorised&&!sheet},
      {label:'Ready to work',done:Boolean(setupComplete?.registered&&setupComplete?.verifiedBackup),active:Boolean(sheet)&&!(setupComplete?.registered&&setupComplete?.verifiedBackup)}
    ].map((item,index)=><div key={item.label} className={'rounded-xl border p-3 flex items-center gap-2 text-xs font-semibold '+(item.done?'bg-emerald-50 border-emerald-200 text-emerald-900':item.active?'bg-blue-50 border-blue-300 text-blue-900':'bg-slate-50 border-slate-200 text-slate-500')}>
      <span aria-hidden="true">{item.done?<Fi.FiCheckCircle size={17}/>:<span className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-current">{index+1}</span>}</span>
      <span>{item.label}</span>
    </div>)}
  </div>
  {account&&sheet&&!getBaseline(sheet)&&
    <div role="status" aria-label="Existing Google Sheet needs verification on this browser"
      className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
      <p className="flex items-center gap-2 font-bold"><Fi.FiAlertCircle aria-hidden="true" size={18}/> Existing workbook detected · verify this browser</p>
      <p className="mt-2 leading-relaxed">A new domain, browser or device has its own local financial storage. Your registered Google Sheet has been found, but this browser has not verified its saved data. <strong>Do not create a second workbook or assume your existing records were transferred.</strong></p>
      <p className="mt-2 leading-relaxed">Authorise Google, then use <strong>Check Sheet</strong> below. If this browser is empty and your previous transactions are in Google, use <strong>Restore from Sheet</strong> after reviewing the saved revision. Nothing will be restored automatically.</p>
    </div>}
  {!APP_ID&&<p role="alert" className="text-amber-700 bg-amber-50 p-3 rounded-lg text-sm">BPA Applications Hub needs the permanent VITE_BPA_APP_ID configured by the administrator.</p>}
  {!isGoogleConfigured()&&<p role="alert" className="text-amber-700 bg-amber-50 p-3 rounded-lg text-sm">Google connection needs VITE_GOOGLE_CLIENT_ID configured and this website origin authorised in Google Cloud.</p>}
  {busy&&!['create','connect','google'].includes(busy)&&<p role="status" className="text-sm font-medium text-blue-700 bg-blue-50 rounded-lg p-3">Processing {busy}…</p>}
  {error&&<p role="alert" className="text-rose-700 bg-rose-50 rounded-lg p-3 text-sm">{error}</p>}
  {message&&<p role="status" className="text-green-800 bg-green-50 rounded-lg p-3 text-sm">{message}</p>}
  <div className="border border-slate-200 rounded-xl p-4 space-y-3">
   <h3 className="font-bold text-slate-900 flex items-center gap-2"><Fi.FiUserCheck/> 1. Your BPA account</h3>
   {account?<div className="flex flex-wrap justify-between items-center gap-3">
      <p className="text-sm text-emerald-800"><Fi.FiCheckCircle className="inline mr-2"/>Signed in as <strong>{account.record.email}</strong></p>
      <button type="button" disabled={Boolean(busy)} className={btn+' border border-slate-200'} onClick={logOut}>Sign out of BPA</button>
    </div>:
    <form onSubmit={signIn} className="grid sm:grid-cols-2 gap-3">
      <input aria-label="BPA customer email" type="email" autoComplete="email" required placeholder="BPA customer email" value={email} onChange={e=>setEmail(e.target.value)} className="border border-slate-200 rounded-xl px-3 py-3 min-w-0"/>
      <input aria-label="BPA password" type="password" autoComplete="current-password" required placeholder="Password" value={password} onChange={e=>setPassword(e.target.value)} className="border border-slate-200 rounded-xl px-3 py-3 min-w-0"/>
      <div className="sm:col-span-2 flex flex-wrap gap-3">
        <button disabled={Boolean(busy)||!APP_ID} className={btn+' bg-blue-600 text-white'}>Sign in and verify access</button>
        <button type="button" className={btn+' border border-slate-200'} onClick={()=>run('reset',async()=>{await requestPasswordReset(email);setMessage('Password reset email requested if the address is registered.')})}>Reset password</button>
      </div>
    </form>}
  </div>
  {account&&<div className="border border-slate-200 rounded-xl p-4 space-y-3">
    <h3 className="font-bold text-slate-900 flex items-center gap-2"><Fi.FiKey/> 2. Connect your Google account</h3>
    {authorised?<p role="status" className="flex items-start gap-2 text-sm text-emerald-800 bg-emerald-50 rounded-xl p-3"><Fi.FiCheckCircle className="mt-0.5 shrink-0"/>Google is authorised for this browser session. {sheet?'Your linked workbook is shown below.':'Next, choose whether to create a new workbook or connect an existing one.'}</p>:
      <p className="text-sm text-slate-600">Choose the Google account where your spreadsheet should live. Google will open an authorisation window. Nothing is created until you choose a workbook below.</p>}
    <button type="button" disabled={Boolean(busy)||!configured} className={btn+' bg-[#0176D3] text-white disabled:opacity-60 inline-flex items-center gap-2'} onClick={connectGoogle}>
      {busy==='google'?<Fi.FiLoader className="animate-spin"/>:<Fi.FiCloud/>}
      {busy==='google'?'Waiting for Google permission…':authorised?'Reconnect Google':'Authorise Google'}
    </button>
    {busy==='google'&&<p role="status" className="text-xs text-blue-700">Check the Google sign-in window and select your account. Return here after approving.</p>}
    {googleStatus==='error'&&<p className="text-sm text-amber-700">Google authorisation did not complete. Check whether your browser blocked the sign-in window, then try again.</p>}
  </div>}
  {account&&<div className="border border-slate-200 rounded-xl p-4 space-y-4">
    <h3 className="font-bold text-slate-900 flex items-center gap-2"><Fi.FiGrid/> 3. Choose your Google spreadsheet</h3>
    {!authorised&&<p className="rounded-xl bg-slate-50 text-slate-600 p-3 text-sm">First complete Google authorisation in Step 2. Then your workbook choices will be available.</p>}
    {sheet&&<div className="rounded-xl bg-blue-50 border border-blue-100 p-3 text-sm space-y-2">
      <p className="font-semibold text-blue-900"><Fi.FiLink className="inline mr-2"/>We found a linked workbook</p>
      <p className="break-all text-slate-600">Google Sheet ID: <strong>{sheet}</strong></p>
      <p className="text-slate-600">Use the same workbook. Do not create another unless you intentionally need a separate workspace.</p>
      <a href={'https://docs.google.com/spreadsheets/d/'+encodeURIComponent(sheet)+'/edit'} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-semibold text-[#0176D3]"><Fi.FiExternalLink/> Open my Google Sheet</a>
      {registerPending&&<p className="text-amber-800 font-semibold">BPA registration is pending. Your existing workbook reference is preserved.</p>}
      {registerPending&&<button type="button" disabled={Boolean(busy)||!authorised} onClick={retryRegistry} className={btn+' bg-amber-50 text-amber-900 border border-amber-200 disabled:opacity-50'}>Retry registration</button>}
    </div>}
    {!sheet&&<div className="grid sm:grid-cols-2 gap-3">
      <button type="button" disabled={Boolean(busy)||!authorised} onClick={()=>{setWorkbookChoice('new');setConfirmNew(true);setSetupProgress(null);setSetupComplete(null)}} className={'text-left rounded-xl border p-4 min-h-28 disabled:opacity-50 '+(workbookChoice==='new'?'border-emerald-500 bg-emerald-50':'border-slate-200 hover:border-emerald-400')}>
        <span className="flex gap-2 items-center text-base font-bold text-slate-900"><Fi.FiPlusCircle className="text-emerald-700"/> Create a new BPA Sheet</span>
        <span className="block text-sm text-slate-600 mt-2">Recommended for your first setup. Creates all worksheets and saves your current records automatically.</span>
      </button>
      <button type="button" disabled={Boolean(busy)||!authorised} onClick={()=>{setWorkbookChoice('existing');setConfirmNew(false);setSetupProgress(null);setSetupComplete(null)}} className={'text-left rounded-xl border p-4 min-h-28 disabled:opacity-50 '+(workbookChoice==='existing'?'border-blue-500 bg-blue-50':'border-slate-200 hover:border-blue-400')}>
        <span className="flex gap-2 items-center text-base font-bold text-slate-900"><Fi.FiLink className="text-blue-700"/> Connect an existing BPA Sheet</span>
        <span className="block text-sm text-slate-600 mt-2">Already have an Expense Manager workbook? Reuse it without overwriting existing records.</span>
      </button>
    </div>}
    {(workbookChoice==='existing'||Boolean(sheet))&&<div className="rounded-xl bg-slate-50 p-3 space-y-3">
      <label className="text-sm font-semibold text-slate-700 block">Existing BPA Google Sheet URL or ID
        <input aria-label="Google Sheet URL or ID" value={sheetInput} onChange={e=>setSheetInput(e.target.value)} placeholder="Paste your BPA Expense Manager Sheet URL" className="block w-full border border-slate-200 rounded-xl px-3 py-3 mt-2 min-w-0 font-normal"/>
      </label>
      <button type="button" disabled={Boolean(busy)||!authorised||!sheetInput.trim()} onClick={connectExisting} className={btn+' bg-blue-600 text-white disabled:opacity-50 inline-flex gap-2 items-center'}>
        {busy==='connect'?<Fi.FiLoader className="animate-spin"/>:<Fi.FiLink/>}{busy==='connect'?'Checking your workbook…':'Connect and verify this workbook'}
      </button>
      <p className="text-xs text-slate-600">Only BPA Expense Manager workbooks with the correct tabs can be connected. Existing financial data will not be silently replaced.</p>
    </div>}
    {confirmNew&&!sheet&&workbookChoice==='new'&&<div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 space-y-3">
      <p className="text-sm text-emerald-900">We'll create <strong>one</strong> BPA Expense Manager workbook in the Google account you just approved, set up the tabs, upload your local records and verify the backup.</p>
      <button type="button" disabled={Boolean(busy)||!authorised} onClick={createNew} className={btn+' bg-emerald-700 text-white disabled:opacity-50 inline-flex items-center gap-2'}>
        {busy==='create'?<Fi.FiLoader className="animate-spin"/>:<Fi.FiCheckCircle/>}
        {busy==='create'?'Creating and verifying…':'Confirm and create my Google Sheet'}
      </button>
      <p className="text-xs text-emerald-900">Once clicked, keep this page open until you see the completion message. Never click Create a second time if setup is interrupted.</p>
    </div>}
    <div ref={progressRef}><GoogleSetupProgress progress={setupProgress}/></div>
    {setupComplete&&setupProgress?.status==='complete'&&<div role="status" className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 space-y-3">
      <h4 className="text-lg font-bold text-emerald-900 flex items-center gap-2"><Fi.FiCheckCircle/> {setupComplete.initialBackup?'Your Google Sheet is ready!':'Your existing Google workbook is connected'}</h4>
      {setupComplete.initialBackup?
        <p className="text-sm text-emerald-900">Your spreadsheet was created or connected, your initial financial backup was verified and the workbook was registered with BPA. You can now start adding transactions.</p>:
        <p className="text-sm text-emerald-900">Your existing workbook was linked without replacing its saved records. If this device is new, review the backup and restore it before uploading changes.</p>}
      <div className="flex flex-wrap gap-2">
        {setupComplete.initialBackup&&<Link to="/transactions" className={btn+' inline-flex items-center gap-2 bg-[#0176D3] text-white'}><Fi.FiPlusCircle/> Start adding transactions</Link>}
        {sheet&&<a href={'https://docs.google.com/spreadsheets/d/'+encodeURIComponent(sheet)+'/edit'} target="_blank" rel="noopener noreferrer" className={btn+' inline-flex items-center gap-2 bg-white border border-emerald-300 text-emerald-900'}><Fi.FiExternalLink/> Open my Google Sheet</a>}
      </div>
    </div>}
    {setupComplete&&!setupComplete.registered&&<p role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Your Google workbook and backup are preserved. The BPA customer registry is not yet confirmed. Use <strong>Retry registration</strong> above before testing another device.</p>}
  </div>}
  {account&&<details className="border border-amber-200 rounded-xl p-4 space-y-3">
    <summary className="cursor-pointer text-sm font-semibold text-amber-900">Used Expense Manager before BPA login? Import original browser data (optional)</summary>
    <div className="pt-3 space-y-3">
      <p className="text-sm text-slate-600">Older local records stay in the original browser database. They are never automatically assigned to your new customer account. Only import if you own those records.</p>
      <label className="flex items-start gap-2 text-sm text-slate-700"><input type="checkbox" className="mt-1" checked={legacyConfirm} onChange={e=>setLegacyConfirm(e.target.checked)}/> I confirm the old local financial data belongs to my BPA account.</label>
      <button type="button" disabled={Boolean(busy)||!legacyConfirm} onClick={importLegacy} className={btn+' bg-amber-50 text-amber-900 border border-amber-200 disabled:opacity-50'}>Import original browser data</button>
      <p className="text-xs text-slate-500">Only works in a new customer workspace without user-entered records. Export a backup if in doubt.</p>
    </div>
  </details>}
  {connected&&<div className="border border-slate-200 rounded-xl p-4 space-y-3">
  <h3 className="font-bold text-slate-900">4. Sync or recover</h3>
  <div className="grid sm:grid-cols-2 gap-3">
   <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 space-y-1">
    <p className="text-xs font-semibold text-slate-500">This device's last verified revision</p>
    <p className="text-sm font-semibold text-slate-800 break-all">{localBase||'Not yet verified on this device'}</p>
   </div>
   <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 space-y-1">
    <p className="text-xs font-semibold text-slate-500">Google's last completed revision</p>
    <p className="text-sm font-semibold text-slate-800 break-all">{remote?.revision||'Not checked in this session'}</p>
   </div>
  </div>
  {!localBase&&<p role="status" className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">This device has not verified a matching Google backup. <strong>Check Sheet</strong> safely compares your records without overwriting anything. If the copies differ, review them before uploading.</p>}
  {!authorised&&<p role="status" className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">Choose <strong>Authorise Google</strong> above to enable the Sheet actions. Disabled controls cannot run until Google grants permission.</p>}
  {remote?.counts&&<div className="grid grid-cols-2 sm:grid-cols-3 gap-2" aria-label="Synced record counts">
   {Object.entries({categories:'Categories',categoryRules:'Category rules',recurringRules:'Recurring rules',transactions:'Transactions',budgets:'Budgets',people:'People',ious:'IOUs',settings:'Settings'}).map(([key,label])=><div key={key} className="bg-slate-50 rounded-lg p-2 text-xs text-slate-600"><span>{label}</span><strong className="block text-base text-slate-900">{remote.counts[key]??'—'}</strong></div>)}
  </div>}
  {syncActivity&&<div role={syncActivity.status==='error'?'alert':'status'} aria-live="polite" aria-atomic="true"
    className={'rounded-xl border p-4 flex items-start gap-3 '+(syncActivity.status==='success'
      ?'border-emerald-200 bg-emerald-50 text-emerald-900'
      :syncActivity.status==='warning'
        ?'border-amber-200 bg-amber-50 text-amber-900'
        :syncActivity.status==='error'
          ?'border-rose-200 bg-rose-50 text-rose-900'
          :'border-blue-200 bg-blue-50 text-blue-900')}>
    <span aria-hidden="true" className="mt-0.5 shrink-0">
      {syncActivity.status==='running'?<Fi.FiLoader className="animate-spin" size={20}/>:
       syncActivity.status==='success'?<Fi.FiCheckCircle size={20}/>:
       <Fi.FiAlertCircle size={20}/>}
    </span>
    <div className="space-y-1 min-w-0">
      <p className="text-sm font-bold">{syncActivity.key==='check'?'Checking your Google Sheet':
        syncActivity.key==='sync'?'Backing up your financial data':
        syncActivity.key==='format'?'Repairing Sheet formatting':
        syncActivity.key==='download'?'Downloading Google backup':'Restoring your records'}</p>
      <p className="text-sm leading-6 break-words">{syncActivity.message}</p>
      {syncActivity.status==='running'&&<p className="text-xs opacity-80">Keep this page open. Google may take several moments to validate the workbook and each saved record.</p>}
    </div>
   </div>}
  <div className="flex min-w-0 flex-wrap gap-2" aria-label="Google Sheet actions">
   <button type="button" title={authorised?'Verify the Sheet and compare your data':'Authorise Google first'} disabled={Boolean(busy)||!authorised} onClick={checkRemote} className={btn+' inline-flex items-center gap-2 border border-slate-200 bg-white text-[#032D60] hover:border-blue-300 hover:bg-blue-50'}>{busy==='check'?<Fi.FiLoader className="animate-spin"/>:<Fi.FiCheckCircle/>} {busy==='check'?'Checking Google…':'Check Sheet'}</button>
   <button type="button" title={!authorised?'Authorise Google first':!localBase?'Use Check Sheet to verify the local revision first':'Upload local changes now'} disabled={Boolean(busy)||!authorised||!localBase} onClick={syncNow} className={btn+' inline-flex items-center gap-2 bg-[#0176D3] text-white hover:bg-[#0065b6]'}>{busy==='sync'?<Fi.FiLoader className="animate-spin"/>:<Fi.FiRefreshCw/>} {busy==='sync'?'Uploading…':'Sync Now (upload)'}</button>
   <button type="button" title={authorised?'Repair formatting without changing data':'Authorise Google first'} disabled={Boolean(busy)||!authorised} onClick={repairRows} className={btn+' inline-flex items-center gap-2 border border-slate-200 bg-white text-[#032D60] hover:border-blue-300 hover:bg-blue-50'}>{busy==='format'?<Fi.FiLoader className="animate-spin"/>:<Fi.FiDroplet/>} {busy==='format'?'Repairing…':'Fix Sheet row colours'}</button>
   <button type="button" title={authorised?'Download the latest verified revision':'Authorise Google first'} disabled={Boolean(busy)||!authorised} onClick={downloadRemote} className={btn+' inline-flex items-center gap-2 border border-slate-200 bg-white text-[#032D60] hover:border-blue-300 hover:bg-blue-50'}>{busy==='download'?<Fi.FiLoader className="animate-spin"/>:<Fi.FiDownload/>} {busy==='download'?'Downloading…':'Download Sheet backup'}</button>
   <button type="button" title={authorised?'Review restore options':'Authorise Google first'} disabled={Boolean(busy)||!authorised} onClick={()=>setRecoverMode(v=>!v)} className={btn+' inline-flex items-center gap-2 border border-amber-300 bg-white text-amber-900 hover:bg-amber-50'}><Fi.FiRotateCcw/> Restore from Sheet</button>
  </div>
  {recoverMode&&<div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
   <p className="text-sm text-amber-900"><strong>Warning:</strong> Restore replaces all local app records with a verified Sheet revision. A JSON backup of your current records is downloaded first. Make sure your browser allows downloads.</p>
   <label className="block text-sm font-semibold text-amber-900">Type RESTORE<input className="block border border-amber-200 rounded-xl p-3 mt-1 w-full" value={confirmation} onChange={e=>setConfirmation(e.target.value)} placeholder="RESTORE"/></label>
   <button type="button" disabled={Boolean(busy)||confirmation!=='RESTORE'} className={btn+' bg-amber-700 text-white disabled:opacity-40'} onClick={restoreRemote}>Confirm restore</button></div>}
  <p className="text-xs text-slate-500">Your Google Sheet is customer-owned backup storage. After setup, this app automatically uploads committed local changes while Google permission is active in this browser session. The cloud status in the top bar confirms completion or warns about unsynced local edits. If you close the app or permission expires, reconnect Google to resume. Sync Now is still available. Simultaneous editing on different devices is not supported; conflicts pause uploads for review.</p>
  </div>}
 </section>
}